import { CalendarIcon, ClockIcon, DesktopComputerIcon, LocationMarkerIcon } from "@heroicons/react/outline";
import { Card, Col, Row } from "react-bootstrap";
import { format, utcToZonedTime } from "date-fns-tz";
import PropTypes from "prop-types";
import { useMemo } from "react";

import { Default, Mobile } from "../../../components/Devices";
import CourseCalendarLinks from "../../courses/components/CourseCalendarLinks";
import CourseCardFooter from "./CourseCardFooter";
import CourseLinkButton from "../../../components/CourseLinkButton";
import MetaItem from "../../../components/MetaItem";
import MobileCourseCard from "../../../components/MobileCourseCard";

import { eventCancelled, isHybridEvent, isInPersonEvent, isRemoteEvent } from "../../../helpers/event";
import { getTimezone } from "../../../helpers/dates";

const today = new Date();
const timeZone = getTimezone();

const CourseCard = ({
	course,
	otherContent,
	registrationType,
	showDescription
}) => {
	const isFutureClass = useMemo(() => today < utcToZonedTime(course.date), [course]);
	const inPersonEvent = useMemo(() => isHybridEvent(course) ? registrationType === "in-person" : isInPersonEvent(course), [course]);
	const remoteEvent = useMemo(() => isHybridEvent(course) ? registrationType === "online" : isRemoteEvent(course), [course]);

	return (
		<>
			<Mobile>
				<MobileCourseCard
					course={course}
					hideRegistrationClosedMessage
					otherContent={otherContent}
					showJoinButton
				/>
			</Mobile>
			<Default>
				<Card className="mb-3 class-card">
					<Card.Body className="p-4">
						<Card.Title className="fs-20 fw-bold text-neutral3 py-2">
							{course.title}
						</Card.Title>

						{showDescription
							? <div className="text-secondary py-2 fs-16" dangerouslySetInnerHTML={{ __html: course.description }} /> : null}

						<Row className="py-2">
							<Col>
								<MetaItem Icon={CalendarIcon} label={format(course.date, "E LLL d, Y")} />
							</Col>

							<Col>
								<MetaItem
									Icon={ClockIcon}
									label={format(utcToZonedTime(course.date, timeZone), "h:mm a z", { timeZone })}
								/>
							</Col>

							<Col>
								{inPersonEvent
									? <MetaItem Icon={LocationMarkerIcon} label={course.location?.name || course.location} /> : null}
								{remoteEvent
									? <MetaItem Icon={DesktopComputerIcon} label="Online" />
									: null}
							</Col>
						</Row>

						{
							eventCancelled(course)
								? null
								: isFutureClass
									? (
										<>
											<CourseCalendarLinks course={course} />
											<CourseLinkButton course={course} />
										</>
									)
									: null
						}

						{otherContent ? otherContent : null}

					</Card.Body>

					{isFutureClass ? <CourseCardFooter course={course} /> : null}
				</Card>
			</Default>
		</>
	);

};

CourseCard.propTypes = {
	course: PropTypes.shape({
		cancelLink: PropTypes.string,
		date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
		description: PropTypes.string,
		id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		location: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.shape({
				name: PropTypes.string
			})
		]),
		moreInfoLink: PropTypes.string,
		rawDate: PropTypes.instanceOf(Date),
		time: PropTypes.string,
		title: PropTypes.string
	}).isRequired,
	otherContent: PropTypes.node,
	registrationType: PropTypes.string,
	showDescription: PropTypes.bool
};

CourseCard.defaultProps = {
	otherContent: null,
	registrationType: "",
	showDescription: false
};

export default CourseCard;
